import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { TopLevelContainer } from '@xo-union/tk-component-grid';

import { Button } from '@xo-union/tk-component-buttons';
import { DisplayButton } from '@xo-union/tk-ui-links';
import { compose } from '@xo-union/react-css-modules';
import { Subhead } from '@xo-union/tk-ui-typography';
import '@xo-union/tk-ui-typography/lib/globals.css';

import FullScreenModalHeader from '../../Modals/FullScreenModal/Header';
import { IconToggle, IconToggleContainer } from './IconToggle';
import { dashboardRoute } from '../../../helpers/routes';
import { IconButton } from '../../IconButton';
import { PreviewState } from '../LiteSitePreview';
import styles from './previewHeader.scss';
import { Snackbar, SnackbarMessage } from '../../Snackbar';
import ShareOrPublishButton from '../../SharedComponents/ShareOrPublishButton/ShareOrPublishButton';
import PurchaseDomainModal from '../../PurchaseDomainModal';

interface Props {
  allowPaperPreview: boolean;
  selectedPreview: PreviewState;
  changeSelectedPreview: (selection: string) => void;
  editWebsiteToggle: () => void;
  className?: string;
  showPurchaseDomain?: boolean;
  setShowPublishedIndexBanner?: (show: boolean) => void;
}

export const PreviewHeader: React.FC<Props> = ({
  allowPaperPreview,
  selectedPreview,
  changeSelectedPreview,
  editWebsiteToggle,
  className = '',
  showPurchaseDomain = true,
  setShowPublishedIndexBanner,
}) => {
  const history = useHistory();
  const [showPurchaseDomainModal, setShowPurchaseDomainModal] = useState(false);
  const toplevelContainerStyles = compose({ 'top-level-container': styles.topLevelContainer });
  const [toastMessage, setToastMessage] = useState({} as SnackbarMessage);

  const renderButtons = () => (
    <>
      <div className={styles.shareButtonWrapper}>
        <ShareOrPublishButton
          setShowPublishedIndexBanner={setShowPublishedIndexBanner}
          trackSource="preview"
          buttonSize="md"
          fullWidth
        />
      </div>
      <div className={styles.buttonEditContainer}>
        <Button
          size="md"
          color="primary"
          onClick={editWebsiteToggle}
          className={styles.editWebsiteButton}
          data-testid="eip-edit-website-btn"
        >
          <span className={styles.desktopCopy}>Edit website</span>
          <span className={styles.mobileCopy}>Edit</span>
        </Button>
      </div>
    </>
  );

  const renderExitPreviewButton = () => {
    return (
      <IconButton
        name="close"
        size="md"
        arialabel="exit preview"
        datatestid="eip-exit-preview-btn"
        onClick={() => history.push(dashboardRoute)}
        className={styles.exitPreviewBtn}
      />
    );
  };

  const renderDesktopHeader = () => {
    const upgradeUrlStyles = compose({ 'display-link': styles.upgradeUrlButton, 'tappable-area': styles.nowrap });

    return (
      <div className={styles.desktopContainer}>
        <div className={styles.leftColumn}>
          {renderExitPreviewButton()}
          {showPurchaseDomain && (
            <DisplayButton
              color="primary"
              classes={upgradeUrlStyles}
              onClick={() => setShowPurchaseDomainModal(true)}
              data-testid="eip-upgrade-your-url-btn"
            >
              Upgrade your URL
            </DisplayButton>
          )}
        </div>
        <div className={styles.centerColumn}>
          <div className={styles.iconToggle}>
            <IconToggleContainer size="md" changeSelectedPreview={changeSelectedPreview}>
              <IconToggle icon="desktop" selected={selectedPreview === 'desktop'} />
              <IconToggle icon="mobile" selected={selectedPreview === 'mobile'} />
              {allowPaperPreview && <IconToggle icon="category-inv" selected={selectedPreview === 'paper'} />}
            </IconToggleContainer>
          </div>
        </div>
        <div className={styles.rightColumn}>{renderButtons()}</div>
      </div>
    );
  };

  const renderMobileHeader = () => {
    return (
      <div className={styles.mobileContainer}>
        <div className={styles.topRow}>
          <Subhead as="h1">Website preview</Subhead>
          {renderExitPreviewButton()}
        </div>
        <div className={styles.bottomRow}>{renderButtons()}</div>
      </div>
    );
  };

  return (
    <>
      <Snackbar clearToastMessage={setToastMessage} toastMessage={toastMessage} />
      {showPurchaseDomainModal ? (
        <PurchaseDomainModal
          onClose={() => setShowPurchaseDomainModal(false)}
          setDomainPaymentToastMessage={setToastMessage}
        />
      ) : null}
      <FullScreenModalHeader className={`${styles.previewHeader} ${className}`}>
        <div>
          <TopLevelContainer classes={toplevelContainerStyles}>
            {renderMobileHeader()}
            {renderDesktopHeader()}
          </TopLevelContainer>
        </div>
      </FullScreenModalHeader>
    </>
  );
};

export default PreviewHeader;
